import React, { useState, useEffect, useReducer } from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import { NoQueueChip } from "../../components/NoQueueWarning";
import ConfirmationModal from "../../components/ConfirmationModal";
import MobileRecordCard, { MobileRecordCardSkeleton } from "../../components/MobileRecordCard";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";

// ---------------------------------------------------------------------------
// Perfil — chip e avatar
// ---------------------------------------------------------------------------
const PROFILE_CONFIG = {
  admin:      { label: "Admin",      bg: "#e3f0fd", color: "#1565c0", avatarBg: "#2D7BE8" },
  supervisor: { label: "Supervisor", bg: "#fff3e0", color: "#e65100", avatarBg: "#f57c00" },
  user:       { label: "Usuário",    bg: "#f5f5f5", color: "#616161", avatarBg: "#9e9e9e" },
};

const getInitials = (name) =>
  name
    ? name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()
    : "?";

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];
    users.forEach((user) => {
      const idx = state.findIndex((u) => u.id === user.id);
      if (idx !== -1) state[idx] = user;
      else newUsers.push(user);
    });
    return [...state, ...newUsers];
  }
  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const idx = state.findIndex((u) => u.id === user.id);
    if (idx !== -1) { state[idx] = user; return [...state]; }
    return [user, ...state];
  }
  if (action.type === "DELETE_USER") {
    const userId = action.payload;
    const idx = state.findIndex((u) => u.id === userId);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  nameCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: "0.75rem",
    fontWeight: 700,
    flexShrink: 0,
  },
  nameText: {
    fontWeight: 500,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
  },
  emailText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  cardTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.disabled,
  },
  emptyIcon: {
    fontSize: 56,
    marginBottom: theme.spacing(1.5),
    opacity: 0.35,
  },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const Users = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_USERS", payload: data.users });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-user`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    });

    return () => socket.disconnect();
  }, []);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => setPageNumber((prev) => prev + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  const renderActions = (user) => (
    <>
      <Tooltip title="Editar">
        <IconButton size="small" onClick={() => handleEditUser(user)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir">
        <IconButton
          size="small"
          onClick={() => {
            setConfirmModalOpen(true);
            setDeletingUser(user);
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  const emptyState = (
    <Box className={classes.emptyState}>
      <PeopleOutlineIcon className={classes.emptyIcon} />
      <Typography variant="body2" gutterBottom>
        {searchParam
          ? `Nenhum usuário encontrado para "${searchParam}"`
          : "Nenhum usuário cadastrado ainda."}
      </Typography>
      {!searchParam && (
        <Button
          size="small"
          color="primary"
          variant="outlined"
          onClick={handleOpenUserModal}
          style={{ marginTop: 8 }}
        >
          Adicionar primeiro usuário
        </Button>
      )}
    </Box>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${deletingUser.name}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />

      <MainHeader>
        <Title>{i18n.t("users.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder="Buscar usuário..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenUserModal}
          >
            {i18n.t("users.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        {!loading && users.length === 0 && emptyState}

        {isMobile ? (
          (users.length > 0 || loading) && (
            <div>
              {users.map((user) => {
                const profileCfg = PROFILE_CONFIG[user.profile] || PROFILE_CONFIG.user;
                return (
                  <MobileRecordCard
                    key={user.id}
                    title={
                      <div className={classes.cardTitleRow}>
                        <Avatar
                          className={classes.avatar}
                          style={{ backgroundColor: profileCfg.avatarBg }}
                        >
                          {getInitials(user.name)}
                        </Avatar>
                        <span>{user.name}</span>
                      </div>
                    }
                    subtitle={user.email}
                    headerAction={renderActions(user)}
                    chips={
                      <>
                      <Chip
                        size="small"
                        label={profileCfg.label}
                        style={{
                          backgroundColor: profileCfg.bg,
                          color: profileCfg.color,
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 22,
                          border: "none",
                        }}
                      />
                      {!user.queues?.length && <NoQueueChip type="user" />}
                      </>
                    }
                  />
                );
              })}
              {loading &&
                [0, 1, 2].map((i) => <MobileRecordCardSkeleton key={i} />)}
            </div>
          )
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="left">{i18n.t("users.table.name")}</TableCell>
                <TableCell align="left">{i18n.t("users.table.email")}</TableCell>
                <TableCell align="center">{i18n.t("users.table.profile")}</TableCell>
                <TableCell align="center">{i18n.t("users.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const profileCfg = PROFILE_CONFIG[user.profile] || PROFILE_CONFIG.user;
                return (
                  <TableRow key={user.id} hover>
                    <TableCell align="left">
                      <div className={classes.nameCell}>
                        <Avatar
                          className={classes.avatar}
                          style={{ backgroundColor: profileCfg.avatarBg }}
                        >
                          {getInitials(user.name)}
                        </Avatar>
                        <Typography className={classes.nameText}>
                          {user.name}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell align="left">
                      <Typography className={classes.emailText}>
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={profileCfg.label}
                        style={{
                          backgroundColor: profileCfg.bg,
                          color: profileCfg.color,
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 22,
                          border: "none",
                        }}
                      />
                      {!user.queues?.length && <NoQueueChip type="user" />}
                    </TableCell>
                    <TableCell align="center">{renderActions(user)}</TableCell>
                  </TableRow>
                );
              })}

              {loading && <TableRowSkeleton columns={4} />}
            </TableBody>
          </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Users;
